'use client';
import LinkLikeButton from '@/src/features/shared/link-like-button';
import RoundedContainer from '@/src/features/ui/rounded-container';
import { ResponseStoreFrontSmeLoanDetail } from '@/src/interfaces/data-contracts';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import fmt from '@/src/utils/helpers/number-format';

import SpecialZone from '@/src/features/sme/detail/modal-content/special-zone';
import { Button } from '@/src/features/ui/button/button';
import { formatInterestRate } from '@/src/utils/helpers/replace-dot-with-comma';
import { useState } from 'react';
import QRComponent from '../../qr-component';
import { useRouter } from 'next/navigation';
import Modal from '@/src/features/ui/modal/modal';
import { CustomerStatusModal } from '@/src/features/shared/modal-content/customer-status-modal';
import { Pages } from '@/src/interfaces/pages-route';

type ContainerProps = {
  withRoundedContainer?: boolean;
  children?: React.ReactNode;
};

const WidgetCreditContainer: React.FC<ContainerProps> = ({ withRoundedContainer, children }) => {
  if (withRoundedContainer)
    return (
      <RoundedContainer className="flex-1 p-4 md:p-10" data-testid="summary-right">
        {children}
      </RoundedContainer>
    );
  else return children;
};

type Props = {
  item: ResponseStoreFrontSmeLoanDetail;
  withRoundedContainer?: boolean;
  isMobile: boolean;
  smeRedirectProductList: Pages.SmeRedirectproductList.ResponseBody;
};

export const WidgetCredit: React.FC<Props> = ({ item, withRoundedContainer, isMobile, smeRedirectProductList }) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [isLoanDisbursementModalOpen, setIsLoanDisbursementModalOpen] = useState(false);
  const router = useRouter();

  // Bu kismin Fibabanka disindaki entegrasyonlar ile birlikte genellestirilmesi gerekiyor.
  const isFibabanka = item.bankId === 5;
  // @ts-ignore
  const isFormIntegration = item.recourseStatus === 1; // 1 = FORM
  const integrationLink = `/ticari-kredi/fibabanka/int/basvuru/form?ProductId=${item.id}&Amount=${item.amount}&Maturity=${item.maturity}&Rate=${item.interestRate}`;

  const integrationInit = () => {
    router.push(integrationLink);
  };

  const handleApplyClickLoanDisbursementModal = () => {
    setIsLoanDisbursementModalOpen(true);
  };
  const handleCloseLoanDisbursementModal = () => {
    setIsLoanDisbursementModalOpen(false);
  };

  const openPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  return (
    <WidgetCreditContainer withRoundedContainer={withRoundedContainer}>
      <SpecialZone
        smeRedirectProductList={smeRedirectProductList}
        data={item}
        setShowModal={setShowModal}
        showModal={showModal}
      />
      <div className="flex flex-col divide-y border-t md:border-0" data-testid="summary-right">
        <div className="flex flex-row justify-between pb-3">
          <div className="text-sm font-semibold text-black600" data-testid="interest-rate-label">
            {item.bank?.financeType === 2 ? 'Kâr Payı Oranı' : 'Faiz Oranı'}
          </div>
          <div className="text-base font-bold text-black900" data-testid="interest-rate">
            %{formatInterestRate(item.interestRate)}
          </div>
        </div>
        <div className="flex flex-row justify-between py-3">
          <div className="text-sm font-semibold text-black600" data-testid="monthly-installment-label">
            Aylık Taksit
          </div>
          <div className="text-base font-bold text-black900" data-testid="monthly-installment">
            {fmt(item.monthlyInstallment, CurrencyTypeMissing.TRY)}
          </div>
        </div>
        <div className="flex flex-row justify-between py-3">
          <div className="text-sm font-semibold text-black600" data-testid="total-amount-label">
            Toplam Maliyet
          </div>
          <div className="text-base font-bold text-black900" data-testid="total-amount">
            {fmt(item.totalAmount, CurrencyTypeMissing.TRY)}
          </div>
        </div>
        <div>
          <div className="flex flex-row justify-between py-3">
            <div className="text-sm text-black500" data-testid="annual-cost-rate-label">
              Yıllık Maliyet Oranı
            </div>
            <div className="text-base font-semibold text-black900" data-testid="annual-cost-rate">
              %{formatInterestRate(item.annualCostRate)}
            </div>
          </div>
          <div className="flex flex-row justify-between">
            <div className="text-sm text-black500" data-testid="expense-amount-label">
              Banka Komisyonu
            </div>
            <div className="text-base font-semibold text-black900" data-testid="expense-amount">
              {item.expenseAmount ? fmt(item.expenseAmount, CurrencyTypeMissing.TRY) : 'Yok'}
            </div>
          </div>
        </div>
        {isFibabanka && item.isActiveIntegration && isFormIntegration ? (
          <Button
            onClick={integrationInit}
            text={'Hemen Başvur'}
            className="applyBtn mt-8 h-[48px] w-full rounded-lg text-sm md:text-base"
          />
        ) : (
          <>
            {item.isActiveRecourse && item.recourseLink && !item.isQrPopUpActive && (
              <LinkLikeButton
                target={item.target}
                href={item.recourseLink}
                text={'Hemen Başvur'}
                className="mt-8"
                data-testid="apply-button-cta"
              />
            )}
            {!isMobile && item.isQrPopUpActive && !item.isQrCustomerPopUpActive && (
              <>
                <Button
                  className="mt-8 flex h-[48px] items-center justify-center rounded-lg border border-purple500 text-sm font-bold md:text-base"
                  text="Hemen Başvur"
                  onClick={openPopup}
                  data-testid="apply-button-cta"
                />
                <QRComponent closePopup={closePopup} isPopupOpen={isPopupOpen} product={item} />
              </>
            )}
            {item.isQrPopUpActive && item.isQrCustomerPopUpActive && (
              <>
                <Modal
                  show={isLoanDisbursementModalOpen}
                  onClose={handleCloseLoanDisbursementModal}
                  customSize
                  className="w-[calc(100%-32px)] rounded-xl px-4 py-5 md:w-[600px] md:px-10 md:py-10"
                >
                  <CustomerStatusModal item={item} />
                </Modal>
                <Button
                  className="mt-8 flex h-[48px] items-center justify-center rounded-lg border border-purple500 text-sm font-bold md:text-base"
                  text="Hemen Başvur"
                  onClick={handleApplyClickLoanDisbursementModal}
                  data-testid="applyButtonLoanDisbursement"
                />
              </>
            )}
            {!item.isActiveRecourse && item.specialZone && (
              <Button
                onClick={() => setShowModal(true)}
                text={'Hemen Başvur'}
                data-testid="apply-button-cta"
                className="applyBtn mt-8 h-[48px] w-full rounded-lg text-sm md:text-base"
              />
            )}
          </>
        )}
      </div>
    </WidgetCreditContainer>
  );
};
